import React, { useEffect, useState } from "react";
import { Plus, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STAGES = [
  { key: "lead", label: "Lead", color: "border-t-slate-400" },
  { key: "analysis", label: "Analisi", color: "border-t-blue-500" },
  { key: "contact", label: "Contatto", color: "border-t-violet-500" },
  { key: "documentation", label: "Documentazione", color: "border-t-cyan-500" },
  { key: "proposal", label: "Proposta", color: "border-t-amber-500" },
  { key: "negotiation", label: "Trattativa", color: "border-t-orange-500" },
  { key: "agreement", label: "Accordo", color: "border-t-emerald-500" },
  { key: "closed", label: "Chiuso", color: "border-t-slate-700" },
];

const empty = { title: "", stage: "lead", value: 0, probability: 20, notes: "", property_id: "", contact_id: "" };

export default function CRM() {
  const [deals, setDeals] = useState([]);
  const [props, setProps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const [d, p, c] = await Promise.all([api.get("/deals"), api.get("/properties"), api.get("/contacts")]);
    setDeals(d.data); setProps(p.data); setContacts(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/deals", { ...form, value: Number(form.value), probability: Number(form.probability) });
      toast.success("Trattativa creata"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dropTo = async (stage) => {
    if (!dragId) return;
    try {
      await api.patch(`/deals/${dragId}/stage`, { stage });
      setDragId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="crm-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">CRM · Pipeline</h1>
          <p className="text-muted-foreground text-sm mt-1">Trascina le trattative tra le fasi per aggiornarle.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-deal-button"><Plus className="w-4 h-4" /> Nuova trattativa</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova trattativa</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Titolo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="deal-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fase</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Valore (€)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Immobile</Label>
                  <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Nessuno</SelectItem>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Contatto</Label>
                  <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Nessuno</SelectItem>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Note</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={submit} disabled={!form.title} data-testid="deal-submit-button">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin">
        {STAGES.map((s) => {
          // include legacy stage bucket if any old deals still hold them
          const legacy = { contact: ["contacted"], negotiation: ["negotiating"], proposal: ["offer"], closed: ["closed_won", "closed_lost"] }[s.key] || [];
          const stageDeals = deals.filter((d) => d.stage === s.key || legacy.includes(d.stage));
          const total = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
          return (
            <div key={s.key}
              className={`min-w-[280px] w-[280px] bg-muted/40 rounded-md border-t-2 ${s.color} flex flex-col`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropTo(s.key)}
              data-testid={`stage-col-${s.key}`}
            >
              <div className="px-4 py-3 flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-sm font-semibold">{s.label}</h3>
                  <p className="text-xs text-muted-foreground tabular-nums">{stageDeals.length} · € {Intl.NumberFormat("it-IT").format(total)}</p>
                </div>
              </div>
              <div className="px-3 pb-3 space-y-2 flex-1 min-h-[100px]">
                {stageDeals.map((d) => (
                  <div key={d.id} draggable onDragStart={() => setDragId(d.id)}
                    className="kanban-card bg-card border border-border/60 rounded-md p-3 cursor-grab active:cursor-grabbing"
                    data-testid={`deal-card-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium flex-1">{d.title}</div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await api.get(`/deals/${d.id}/pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement("a");
                            a.href = url; a.download = `deal_${d.id}.pdf`; a.click();
                            URL.revokeObjectURL(url);
                          } catch (err) { toast.error("Impossibile generare PDF"); }
                        }}
                        className="text-muted-foreground hover:text-primary transition-colors"
                        title="Esporta PDF" data-testid={`pdf-${d.id}`}
                      ><FileDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 tabular-nums">€ {Intl.NumberFormat("it-IT").format(d.value || 0)}</div>
                    {d.notes && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{d.notes}</p>}
                  </div>
                ))}
                {stageDeals.length === 0 && <p className="text-xs text-muted-foreground/60 text-center py-8">Trascina qui</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
